/**
 * <product-photo-tint>
 * ---------------------------------------------------------------------------
 * Dos responsabilidades independientes sobre la(s) foto(s) comodín del
 * producto:
 *
 *   1. Teñir: repinta la foto según el color elegido, en vez de mostrar/
 *      ocultar fotos por color (eso lo hace <product-gallery>). Modo
 *      mutuamente excluyente: ver `enable_color_tint` en
 *      sections/main-product.liquid. Técnica: un filtro SVG duotono (ver
 *      snippets/product-image-tint.liquid) que mapea negro→negro y
 *      blanco→el hex de la muestra. Escucha `variant:change` (emitido por
 *      <variant-picker>) y reescribe las tres tablas del filtro (una por
 *      canal R/G/B) con el hex recibido. El servidor ya entrega el filtro
 *      resuelto para la variante inicial, así que no hay parpadeo al cargar.
 *
 *   2. Miniaturas: igual que <product-gallery> en
 *      snippets/product-media-gallery.liquid — clic en una miniatura desplaza
 *      hasta esa foto, y la miniatura activa sigue el scroll.
 */

class ProductPhotoTint extends HTMLElement {
  connectedCallback() {
    this.funcs = {
      r: this.querySelector('[data-tint-func="r"]'),
      g: this.querySelector('[data-tint-func="g"]'),
      b: this.querySelector('[data-tint-func="b"]'),
    };

    document.addEventListener('variant:change', this.#onVariantChange);

    this.stage = this.querySelector('[data-tint-stage]');
    this.thumbsContainer = this.querySelector('[data-tint-thumbs]');
    this.frames = Array.from(this.querySelectorAll('[data-tint-frame]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-tint-thumb]'));

    this.thumbsContainer?.addEventListener('click', this.#onThumbClick);
    this.stage?.addEventListener('scroll', this.#onStageScroll, { passive: true });
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this.#onVariantChange);
  }

  #onVariantChange = (event) => {
    // El evento sube hasta `document`, y puede haber más de un selector de
    // variantes en la página (el modal de compra rápida de un producto
    // relacionado, por ejemplo) — mismo guard que <product-gallery>.
    if (event.detail.sectionId !== this.dataset.sectionId) return;

    this.setTint(event.detail.colorHex);
  };

  setTint(hex) {
    const rgb = ProductPhotoTint.#hexToUnitRgb(hex);

    if (!rgb) {
      this.style.setProperty('--tint-filter', 'none');
      return;
    }

    this.funcs.r?.setAttribute('tableValues', `0 ${rgb[0]}`);
    this.funcs.g?.setAttribute('tableValues', `0 ${rgb[1]}`);
    this.funcs.b?.setAttribute('tableValues', `0 ${rgb[2]}`);
    this.style.setProperty('--tint-filter', `url(#${this.dataset.filterId})`);
  }

  /** '#212123' → [0.129, 0.129, 0.137]. null si el hex no es válido. */
  static #hexToUnitRgb(hex) {
    const match = /^#?([0-9a-f]{6})$/i.exec(hex ?? '');
    if (!match) return null;

    const value = parseInt(match[1], 16);
    return [
      Math.round(((value >> 16) & 255) / 255 * 1000) / 1000,
      Math.round(((value >> 8) & 255) / 255 * 1000) / 1000,
      Math.round((value & 255) / 255 * 1000) / 1000,
    ];
  }

  #onThumbClick = (event) => {
    const thumb = event.target.closest('[data-tint-thumb]');
    if (!thumb) return;

    const target = this.frames.find((frame) => frame.dataset.mediaId === thumb.dataset.mediaId);
    if (target) this.#scrollTo(target);
  };

  #scrollTo(frame, { instant = false } = {}) {
    this.stage?.scrollTo({
      left: frame.offsetLeft - this.stage.offsetLeft,
      behavior: instant || window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth',
    });
    this.#setActiveThumb(frame.dataset.mediaId);
  }

  /** Mantiene la miniatura activa sincronizada mientras el usuario desliza. */
  #onStageScroll = () => {
    if (this.scrollFrame) return;
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      const center = this.stage.scrollLeft + this.stage.clientWidth / 2;
      const current = this.frames.find(
        (frame) => frame.offsetLeft <= center && frame.offsetLeft + frame.offsetWidth > center
      );
      if (current) this.#setActiveThumb(current.dataset.mediaId);
    });
  };

  #setActiveThumb(mediaId) {
    this.thumbs.forEach((thumb) => {
      thumb.classList.toggle('is-active', thumb.dataset.mediaId === mediaId);
    });
  }
}

if (!customElements.get('product-photo-tint')) {
  customElements.define('product-photo-tint', ProductPhotoTint);
}
